import { useEffect,useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCopy, FileText, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import api,{formatApiErrorDetail} from "@/lib/api";

const channels=[
  ["facebook","Facebook ad copy"],["instagram","Instagram caption"],
  ["landing_page","Landing-page copy"],["email","Permission-based email"],
  ["sms","Permission-based SMS"],["organic_search","Search / SEO article excerpt"],
];
const cls="mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-sm";

export default function ContentStudio(){
  const [drafts,setDrafts]=useState([]);
  const [draftId,setDraftId]=useState("");
  const [channel,setChannel]=useState("facebook");
  const [tone,setTone]=useState("professional");
  const [language,setLanguage]=useState("en");
  const [assets,setAssets]=useState([]);
  const [busy,setBusy]=useState(false);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [approvalRefs,setApprovalRefs]=useState({});
  useEffect(()=>{
    let active=true;
    api.get("/platform/campaign-director/drafts").then(({data})=>{
      if(!active)return;
      const ds=data||[];
      setDrafts(ds);setDraftId(ds[0]?.id||"");
    }).catch(e=>{if(active)setError(formatApiErrorDetail(e?.response?.data?.detail)||"Could not load saved campaigns.");})
    .finally(()=>{if(active)setLoading(false);});
    return()=>{active=false};
  },[]);
  useEffect(()=>{
    if(!draftId){setAssets([]);return;}
    let active=true;
    api.get(`/platform/campaign-director/drafts/${draftId}/assets`).then(({data})=>{
      if(active)setAssets((data||[]).slice().reverse());
    }).catch(e=>{if(active){setAssets([]);setError(formatApiErrorDetail(e?.response?.data?.detail)||"Could not load saved content.");}});
    return()=>{active=false};
  },[draftId]);
  const generate=async()=>{
    if(!draftId||busy)return;
    if(!window.confirm("Generate two or three AI text creatives? This uses your OpenAI API credits. Nothing will be published, texted, emailed or advertised."))return;
    setBusy(true);setError("");setNotice("");
    try {
      const {data}=await api.post(`/platform/campaign-director/drafts/${draftId}/assets`,{channel,tone,language});
      setAssets(rows=>[data,...rows]);
      setNotice("Draft creatives saved for review. No content has been published or sent.");
    }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Unable to generate marketing creatives.");}
    finally{setBusy(false);}
  };
  const approveCopy=async item=>{
    const reviewReference=(approvalRefs[item.id]||"").trim();
    if(busy||reviewReference.length<8)return;
    if(!window.confirm("Approve ALL displayed variants after independently reviewing the claims, disclosures, privacy and applicable advertising rules? This does not publish or spend money."))return;
    setBusy(true);setError("");setNotice("");
    try{
      const {data}=await api.post(`/platform/campaign-director/assets/${item.id}/approve`,{
        review_reference:reviewReference,confirm_factual_and_legal_review:true
      });
      setAssets(items=>items.map(x=>x.id===item.id?{...x,status:data.status,
        approved_by:data.approved_by,approval_reference:reviewReference}:x));
      setNotice("Creative approval recorded. Content remains unpublished and no advertising spend was authorized.");
    }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Approval was not recorded.");}
    finally{setBusy(false);}
  };
  const copy=async(text)=>{
    try{await navigator.clipboard.writeText(text);setNotice("Creative text copied. Review disclosures and permissions before using it.");}
    catch{setError("Clipboard unavailable. Select and copy the text manually.");}
  };
  const selected=drafts.find(d=>d.id===draftId);
  return <main className="mx-auto max-w-6xl space-y-6 pb-12" data-testid="content-studio-page">
    <header className="rounded-[22px] bg-[#0B1B2C] px-5 py-7 text-white sm:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D1A656]">Fortunate Operations / Marketing</p>
      <h1 className="mt-3 font-heading text-3xl font-bold">AI Content Studio</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-300">Generate usable, channel-specific marketing copy from your campaign strategy. Compare variants and copy reviewed text to your selected publishing channel.</p>
    </header>
    {error&&<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">{error}</p>}
    {notice&&<p role="status" className="rounded-lg border border-slate-200 bg-white p-3 text-sm">{notice}</p>}
    <section className="grid gap-6 rounded-2xl border bg-card p-4 sm:p-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <div className="space-y-4">
        <h2 className="font-heading text-xl font-semibold">Generate new creatives</h2>
        <label className="block text-sm font-medium">Campaign strategy
          <select className={cls} value={draftId} onChange={e=>setDraftId(e.target.value)} data-testid="studio-draft">
            {!drafts.length&&<option value="">No saved strategy</option>}
            {drafts.map(d=><option key={d.id} value={d.id}>{d.proposal?.name||d.id}</option>)}
          </select>
        </label>
        <label className="block text-sm font-medium">Publishing channel
          <select className={cls} value={channel} onChange={e=>setChannel(e.target.value)} data-testid="studio-channel">
            {channels.map(([value,label])=><option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm font-medium">Tone
            <select className={cls} value={tone} onChange={e=>setTone(e.target.value)}>
              <option value="professional">Professional</option><option value="warm">Warm</option><option value="direct">Direct</option>
            </select>
          </label>
          <label className="block text-sm font-medium">Language
            <select className={cls} value={language} onChange={e=>setLanguage(e.target.value)}>
              <option value="en">English</option><option value="es">Spanish</option>
            </select>
          </label>
        </div>
        <button type="button" onClick={generate} disabled={busy||loading||!draftId}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-[#D1A656] px-5 py-2.5 text-sm font-semibold text-[#0B1B2C] disabled:opacity-40"
          data-testid="studio-generate">
          {busy?<Loader2 className="h-4 w-4 animate-spin"/>:<Sparkles className="h-4 w-4"/>}
          {busy?"Generating…":"Generate 2–3 AI variants"}
        </button>
        {!drafts.length&&<p className="text-sm text-slate-600">
          <Link to="/campaign-director" className="underline">Create your first AI campaign strategy</Link> before generating content.
        </p>}
        {selected&&<p className="rounded-lg border bg-slate-50 p-3 text-xs text-slate-600">
          Source: {selected.proposal?.headline||"Untitled campaign"} — all variants remain unpublished and require review.
        </p>}
      </div>
      <aside className="rounded-xl border bg-[#FBF7EF] p-5">
        <ShieldCheck className="mb-3 h-6 w-6 text-[#AC8441]"/>
        <h2 className="font-heading font-semibold">Production boundaries</h2>
        <p className="mt-2 text-sm leading-6 text-slate-700">AI writes draft copy; it cannot decide insurance eligibility or verify advertising, privacy, and consent compliance.</p>
        <p className="mt-3 text-sm leading-6 text-slate-700">Publishing to Google, Meta, email, SMS or search sites is not connected here. Every creative requires human approval before use.</p>
        <Link to="/campaign-launch" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold underline">
          <FileText className="h-4 w-4"/>Open three-step campaign launch
        </Link>
      </aside>
    </section>
    <section className="space-y-4" aria-label="Saved creative variants">
      <div>
        <h2 className="font-heading text-xl font-bold">Creative library</h2>
        <p className="text-xs text-slate-500">Review-required · Text assets only · No automatic posting</p>
      </div>
      {!assets.length&&<p className="rounded-xl border bg-card p-6 text-sm text-slate-500">No creatives saved for this campaign.</p>}
      {assets.map(item=><article key={item.id} className="space-y-4 rounded-xl border bg-card p-4 sm:p-6" data-testid="studio-asset">
        <div className="flex flex-wrap justify-between gap-3">
          <strong className="text-sm">{channels.find(x=>x[0]===item.channel)?.[1]||item.channel} · {item.language?.toUpperCase()}</strong>
          <span className="rounded-full border px-2 py-1 text-xs text-slate-500">{item.status==="approved"?"Approved, unpublished":"Human review required"}</span>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {(item.variants||[]).map((v,i)=><div key={i} className="min-w-0 space-y-3 rounded-lg border bg-slate-50 p-4 text-sm">
            <div className="text-xs font-semibold text-[#AC8441]">Variant {i+1}</div>
            <h3 className="break-words font-semibold">{v.headline}</h3>
            <p className="whitespace-pre-wrap break-words leading-6">{v.body}</p>
            <p className="font-semibold">CTA: {v.cta}</p>
            <button type="button" onClick={()=>copy(`${v.headline}\n\n${v.body}\n\n${v.cta}`)}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold"
              data-testid="studio-copy"><ClipboardCopy className="h-4 w-4"/>Copy variant</button>
          </div>)}
        </div>
        {item.status==="review_required"&&<div className="rounded-lg border bg-slate-50 p-3 text-sm">
          <p className="text-xs text-slate-600">Review every variant before approval. Provide the independent factual, legal and consent-copy review reference.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input className="min-w-[210px] flex-1 rounded-lg border bg-white px-3 py-2"
              value={approvalRefs[item.id]||""} maxLength={200}
              placeholder="Compliance review reference"
              onChange={e=>setApprovalRefs(old=>({...old,[item.id]:e.target.value}))}/>
            <button type="button" className="rounded-lg border px-4 py-2 font-semibold disabled:opacity-40"
              onClick={()=>approveCopy(item)} disabled={busy||(approvalRefs[item.id]||"").trim().length<8}
              data-testid="studio-approve-copy">Approve reviewed copy</button>
          </div>
        </div>}
        {item.status==="approved"&&<p className="text-xs text-slate-600">Approved copy only · Reference: {item.approval_reference||"recorded"} · No publishing or ad spend</p>}
      </article>)}
    </section>
  </main>;
}
